/* eslint-disable react/react-in-jsx-scope */
import {Box, Button, Card, Grid, Link, Popper, Slider, Typography} from '@material-ui/core';
import {makeStyles, withStyles} from '@material-ui/core/styles';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import {useEffect, useRef, useState} from 'react';
import SlidesContainer, {Settings} from 'react-slick';

interface ClientProgressProps {
    stage: number;
}

const useStyles = makeStyles((theme) => ({
    progressBar: {
        marginBottom: theme.spacing(2),
    },
    tooltipCard: {
        padding: 32,
        paddingTop: 16,
        minWidth: 320,
        maxWidth: 320,
    },
    dotsClass: {
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
    },
}));

const CustomSlider = withStyles((theme) => ({
    markLabel: {
        color: theme.palette.primary.main,
        // left: 'calc(-50%+ 20px)',
        top: -60,
        fontSize: 10,
        '& *': {
            background: 'transparent',
            color: '#000',
        },
        fontWeight: 'bold',
        [theme.breakpoints.up('sm')]: {
            fontSize: 12,
        },
        [theme.breakpoints.up('md')]: {
            fontSize: 14,
        },
        [theme.breakpoints.up('lg')]: {
            fontSize: 18,
        },
        [theme.breakpoints.up('xl')]: {
            fontSize: 24,
        },
    },
    track: {
        height: 8,
        borderRadius: 4,
    },
    rail: {
        height: 8,
        borderRadius: 4,
        color: '#E9ECFF',
        opacity: 1,
    },
    thumb: {
        height: 24,
        width: 24,
        marginTop: -8,
        marginLeft: -12,
    },
}))(Slider);

const settings: Settings = {
    dots: true,
    infinite: false,
    speed: 1,
    slidesToShow: 1,
    slidesToScroll: 1,
};

function ValueLabelComponent(props: any) {
    const {children, value} = props;
    // this value is used to control the the 'completed' mark on the progress bar , if we don't need it for some values, we can write a condition for it.
    const threshold = 100;

    return (
        <>
            {/* condition */}
            {value <= threshold && (
                <span
                    style={{
                        top: -24,
                        position: 'absolute',
                        left: `calc(${value}%)`,
                        fontWeight: 'bold',
                        transform: 'translate(-50%)',
                    }}
                >
                    Completed
                </span>
            )}
            {children}
        </>
    );
}

export function ProgressBar({stage}: {stage: number}) {
    const threshold = 1 + (stage - 1) * 33;
    const markersArray = [0, 33, 66, 100];
    return (
        <CustomSlider
            marks={[
                {value: 0, label: 'Needs Analysis'},
                {value: 33, label: 'Recommendation'},
                {value: 66, label: 'Selected Product'},
                {value: 100, label: 'Apply'},
            ]}
            value={markersArray.filter((ele) => ele <= threshold)}
            color="primary"
            valueLabelDisplay="on"
            ValueLabelComponent={ValueLabelComponent}
        />
    );
}

export default function ClientProgress({stage}: ClientProgressProps) {
    const buttonElement = useRef(null);
    const [open, setOpen] = useState(false);

    const classes = useStyles();
    useEffect(() => {
        // try writing it using useCallback as the function always lost reference and didn't work

        document.addEventListener('click', (e: MouseEvent) => {
            let clickedElseWhere = true;
            e.composedPath().forEach((ele: any) => {
                const {classList} = ele;
                if (classList && (classList.contains('slide-container') || classList.contains('show-info-tooltip')))
                    clickedElseWhere = false;
            });

            if (clickedElseWhere) {
                setOpen(false);
            }
        });
    }, []);

    return (
        <>
            <Grid
                container
                xs={12}
                justify="center"
                style={{margin: '36px 0px 72px 0px'}}
                spacing={1}
                alignItems="center"
            >
                <Grid item alignItems="center">
                    <Typography
                        color="primary"
                        style={{
                            fontWeight: 'bolder',
                            fontSize: 24,
                            lineHeight: '33.6px',
                            display: 'inline-block',
                        }}
                    >
                        Client Progress
                    </Typography>
                </Grid>
                <Grid item xs={1}>
                    <Button
                        ref={buttonElement}
                        style={{minWidth: 0}}
                        onClick={(e) => {
                            setOpen((prev) => !prev);
                        }}
                    >
                        <InfoOutlinedIcon color="primary" className="show-info-tooltip" />
                    </Button>

                    <Popper open={open} anchorEl={buttonElement.current} placement="bottom">
                        <Card className={[classes.tooltipCard, 'slide-container'].join(' ')}>
                            <SlidesContainer {...settings}>
                                <Box>
                                    <Typography variant="h6">Needs Analysis</Typography>
                                    <Typography style={{fontSize: 16, display: 'inline'}}>
                                        If this step is complete, it means the prospect went to our website and
                                        completed the Walkthrough by filling out all required information and submitted
                                        their information to our
                                    </Typography>
                                    <Link
                                        style={{fontSize: 16}}
                                        underline="always"
                                        href="https://quotes.getamplifylife.com/"
                                        target="_blank"
                                    >
                                        {' '}
                                        Lead Form.
                                    </Link>
                                </Box>
                                <Box>
                                    <Typography variant="h6">Recommendation</Typography>
                                    <Typography style={{fontSize: 16, display: 'inline'}}>
                                        If this step is complete, it means the prospect was shown a recommendation. This
                                        happens after they complete the
                                    </Typography>
                                    <Link
                                        style={{fontSize: 16}}
                                        underline="always"
                                        href="https://quotes.getamplifylife.com/"
                                        target="_blank"
                                    >
                                        {' '}
                                        Walkthrough
                                    </Link>
                                </Box>
                                <Box>
                                    <Typography variant="h6">Selected Product</Typography>
                                    <Typography style={{fontSize: 16, display: 'inline'}}>
                                        If this step is complete, it means the prospect actually selected a Product.
                                        They may have adjusted the monthly premium.
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="h6">Apply</Typography>
                                    <Typography style={{fontSize: 16, display: 'inline'}}>
                                        If this step is completed, the prospect completed all of the eApp steps on the
                                    </Typography>
                                    <Link
                                        style={{fontSize: 16}}
                                        underline="always"
                                        href="https://portal.getamplifylife.com"
                                        target="_blank"
                                    >
                                        {' '}
                                        Customer Portal
                                    </Link>
                                    <Typography style={{fontSize: 16, display: 'inline'}}>
                                        {' '}
                                        and selected to either Submit their application digitally or Scheduled an
                                        appointment to speak with an agent.
                                    </Typography>
                                </Box>
                            </SlidesContainer>
                        </Card>
                    </Popper>
                </Grid>
            </Grid>
            <Grid container xs={12} justify="center">
                <Grid item xs={8}>
                    <Box className={classes.progressBar}>
                        <ProgressBar stage={stage} />
                    </Box>
                </Grid>
            </Grid>
        </>
    );
}
